export const IDLE_DETECTION_SUSPENDED_EVENT = "idle_detection_suspended";
export const IDLE_DETECTION_RESUMED_EVENT = "idle_detection_resumed";

let suspensionCount = 0;

function dispatchIdleDetectionEvent(eventName) {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(eventName, {
      detail: { suspensionCount }
    })
  );
}

export function isIdleDetectionSuspended() {
  return suspensionCount > 0;
}

export function suspendIdleDetection(reason = "unspecified") {
  void reason;

  suspensionCount += 1;

  if (suspensionCount === 1) {
    dispatchIdleDetectionEvent(IDLE_DETECTION_SUSPENDED_EVENT);
  }

  let released = false;

  return function releaseIdleDetectionSuspension() {
    if (released) {
      return;
    }

    released = true;
    suspensionCount = Math.max(0, suspensionCount - 1);

    if (suspensionCount === 0) {
      dispatchIdleDetectionEvent(IDLE_DETECTION_RESUMED_EVENT);
    }
  };
}

export async function withIdleDetectionSuspended(callback, reason) {
  const releaseIdleDetectionSuspension = suspendIdleDetection(reason);

  try {
    return await callback();
  } finally {
    releaseIdleDetectionSuspension();
  }
}

function isWindowForegrounded() {
  if (typeof document === "undefined") {
    return true;
  }

  const isVisible = typeof document.visibilityState !== "string" || document.visibilityState === "visible";
  const hasFocus = typeof document.hasFocus !== "function" || document.hasFocus();

  return isVisible && hasFocus;
}

export async function waitForWindowForeground({ timeoutMs = 5000 } = {}) {
  if (typeof window === "undefined" || typeof window.addEventListener !== "function" || isWindowForegrounded()) {
    return;
  }

  await new Promise(resolve => {
    let timeoutId = null;

    const maybeResolve = () => {
      if (!isWindowForegrounded()) {
        return;
      }

      window.removeEventListener("focus", maybeResolve);
      window.removeEventListener("pageshow", maybeResolve);
      document?.removeEventListener?.("visibilitychange", maybeResolve);

      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }

      resolve();
    };

    window.addEventListener("focus", maybeResolve);
    window.addEventListener("pageshow", maybeResolve);
    document?.addEventListener?.("visibilitychange", maybeResolve);

    timeoutId = setTimeout(() => {
      window.removeEventListener("focus", maybeResolve);
      window.removeEventListener("pageshow", maybeResolve);
      document?.removeEventListener?.("visibilitychange", maybeResolve);
      resolve();
    }, timeoutMs);
  });
}

export async function withIdleDetectionSuspendedUntilForeground(callback, reason, options) {
  const releaseIdleDetectionSuspension = suspendIdleDetection(reason);

  try {
    return await callback();
  } finally {
    await waitForWindowForeground(options);
    releaseIdleDetectionSuspension();
  }
}

export function __resetIdleDetectionSuspensionStateForTests() {
  suspensionCount = 0;
}
